import {CopyOutlined} from "@ant-design/icons";
import PropTypes from "prop-types";
import React from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import {isTableDataAvailable} from "../../../common/utils";
import AuthZButton from "../../AuthzButton";
import {AuthZTable, defaultPageSize, defaultPageSizeOptions} from "../../authZTable";
import {successMsg} from "../../simpleMessageAlert";
import "./index.css";
import {UserTableColumnGenerator} from "./userTableColumnGenerator";
import {useSelector} from "react-redux";
import { useIsIndividualInclusionNamingEnabled } from "src/pages/userGroups/KeyCondition/common/hooks/IsIndividualInclusionNamingEnabled";

export const UserTablePropsType = {
  copyEmails: PropTypes.exact({
    copyEmailsList: PropTypes.string,
    tooltipMessage: PropTypes.string,
    alertMessage: PropTypes.string,
    matomoFunction: PropTypes.func,
  }).isRequired,
  label: PropTypes.string.isRequired,
  tableProps: PropTypes.exact({
    tableId: PropTypes.string,
    tableName: PropTypes.string,
    dataSource: PropTypes.array,
    columns: PropTypes.array,
    pagination: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  }).isRequired,
  noUserMessage: PropTypes.string.isRequired,
  individualInclusionMembers: PropTypes.array,
  overridenNoOfUsers: PropTypes.number
};

export function MemberLegend() {
  const isIndividualInclusionNamingEnabled = useIsIndividualInclusionNamingEnabled();
  return (
    <div className={"legend"}>
      <span className={"legend-entry"}>
        <span>*</span> Excluded Member
      </span>
      <span className={"legend-entry"}>
        <span>**</span>{" "}
        {isIndividualInclusionNamingEnabled ? "Individual Inclusion" : "Special Member"}
      </span>
    </div>
  );
}

function UsersTable({copyEmails, label, tableProps, noUserMessage, individualInclusionMembers, groupUniqueName, isGroupMembersTable = false, overridenNoOfUsers = undefined}) {
  const dataSource = tableProps.dataSource;
  const columns = tableProps.columns;
  let paginationProps = false;
  if (tableProps.pagination !== false) {
    paginationProps = {
      showSizeChanger: true,
      pageSizeOptions: defaultPageSizeOptions,
      showTotal: (total, range) =>
        `${range[0]}-${range[1]} of ${total} items`,
      defaultPageSize: defaultPageSize,
      pageSize: tableProps.pagination.pageSize,
      defaultCurrent: 1,
      showQuickJumper: true,
      onShowSizeChange: (current, size) => {
        tableProps.pagination.setTabPageSize({pageSize: size});
      },
    };
  }

  const excludedMembers =
    useSelector((rootState) => rootState.userGroup?.userGroupDetails[groupUniqueName]?.value?.excludedMembers);
  const excludedMemberIds = excludedMembers?.map(x => x.userId) ?? [];
  const isUserExcluded = (user) => Array.isArray(excludedMembers)
    && excludedMembers.find((excludedMember) => excludedMember.userId === user.key)

  const rowClassNameFn = (user) => isUserExcluded(user) ? "member-excluded" : ""

  const modifiedColumns = UserTableColumnGenerator(columns, individualInclusionMembers, true, true, excludedMemberIds);

  function renderCopyEmailButton() {
    const copyEmailsList = copyEmails.copyEmailsList;
    return (
      <span className="copyEmailButton">
        {copyEmailsList.length > 0 ? (
          <CopyToClipboard
            text={copyEmailsList}
            onCopy={() => {
              successMsg(copyEmails.alertMessage);
              copyEmails.matomoFunction();
            }}
          >
            <AuthZButton
              authZButtonType="default"
              tooltipTitle={copyEmails.tooltipMessage}
              tooltipPlacement="top"
              icon={<CopyOutlined/>}
            >
              Copy all emails
            </AuthZButton>
          </CopyToClipboard>
        ) : null}
      </span>
    );
  }

  return (
    <div className="users-info-tab-table">
      {isTableDataAvailable(dataSource) ? (
        <>
          <div className={"users-info-tab-title"}>
            {`${label} (${overridenNoOfUsers ? overridenNoOfUsers : dataSource.length})`} {renderCopyEmailButton()}
            { isGroupMembersTable ? <MemberLegend/>: null }
          </div>
          <AuthZTable
            name={`${tableProps.tableName} ${tableProps.tableId}`}
            id={tableProps.tableId}
            columns={modifiedColumns}
            dataSource={dataSource}
            className="time-table-row-select"
            pagination={paginationProps}
            rowClassName={rowClassNameFn}
          />
        </>
      ) : (
        noUserMessage
      )}
    </div>
  );
}

export default UsersTable;

UsersTable.propTypes = {
  ...UserTablePropsType,
};
